/**
 * The `TaskNode` rendered by xyflow for every non-Input primitive. When the
 * node is a configured Input it delegates to a type-specific subtitle. When
 * it's an unconfigured Input it renders the polymorphic "What kind of input?"
 * picker inline.
 */

import React from 'react';
import clsx from 'clsx';
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowsRightLeftIcon,
  ListBulletIcon,
  InboxArrowDownIcon,
  PencilSquareIcon,
  RectangleStackIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

import {
  CATEGORY_ACCENT,
  INPUT_SUBTYPE_ICON,
  PRIMITIVE_ICON
} from '~/pages/workflows/builder/constants';
import { INPUT_SUBTYPES, getPrimitive } from '~/pages/workflows/builder/fixtures/registry';
import type {
  DefinitionNode,
  InputSubtype,
  ValidationIssue
} from '~/pages/workflows/builder/types';
import {
  effectiveOutputs,
  hasUpstreamList,
  outputRunMode
} from '~/pages/workflows/builder/state/selectors';
import { useBuilder } from '~/pages/workflows/builder/state/context';
import { summarizeConfig } from './node-summary';

export interface TaskNodeData extends Record<string, unknown> {
  node: DefinitionNode;
  issues: ValidationIssue[];
  isSelected: boolean;
  isEnumerable: boolean;
  onAppendAfter: (nodeId: string) => void;
  onInsertParallel: (nodeId: string) => void;
  onRequestSubtype: (nodeId: string, subtype: InputSubtype) => void;
  onSelectNode: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
}

export function TaskNode(props: NodeProps) {
  const { id } = props;
  const data = props.data as TaskNodeData;
  const { node, issues, isSelected, isEnumerable } = data;
  const primitive = getPrimitive(node.taskKey);
  const Icon = PRIMITIVE_ICON[node.taskKey];

  const outputs = effectiveOutputs(node);
  const errors = issues.filter((i) => i.level === 'error');
  const warnings = issues.filter((i) => i.level === 'warning');

  // When a node's output set changes (e.g. the user switches an Input from
  // Document → Spreadsheet, or adds an EXTRACT entity), xyflow keeps stale
  // handle bookkeeping unless we explicitly tell it to re-scan. Without this
  // call, edges that target the new handles render with no line at all.
  const updateNodeInternals = useUpdateNodeInternals();
  const outputFingerprint = outputs.map((o) => o.key).join('|');
  React.useEffect(() => {
    updateNodeInternals(id);
  }, [id, node.taskKey, outputFingerprint, updateNodeInternals]);

  const { state } = useBuilder();

  const isUnconfiguredInput = primitive.category === 'input' && !node.inputSubtype;
  const isOutputBatch =
    primitive.category === 'output' &&
    outputRunMode(node) === 'batch' &&
    hasUpstreamList(state, node);

  const summary = summarizeConfig(node);

  return (
    <div
      className={clsx(
        'group relative rounded-xl border bg-white dark:bg-neutral-800 shadow-sm transition-all',
        isSelected
          ? 'border-primary-500 shadow-lg ring-2 ring-primary-200 dark:ring-primary-500/30'
          : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-white/20',
        errors.length > 0 && !isSelected && 'border-rose-300 dark:border-rose-500/30 shadow-sm',
        'w-[280px]'
      )}
      style={{ minHeight: 110 }}
      onClick={(e) => {
        e.stopPropagation();
        data.onSelectNode(id);
      }}
    >
      {/* Left-gutter "add parallel" affordance */}
      <button
        type="button"
        title="Add parallel step"
        onClick={(e) => {
          e.stopPropagation();
          data.onInsertParallel(id);
        }}
        className="absolute -left-4 top-1/2 z-10 -translate-y-1/2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-1 text-neutral-500 dark:text-neutral-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400"
      >
        <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
      </button>

      {/* Right-edge "append after" affordance */}
      <button
        type="button"
        title="Add next step"
        onClick={(e) => {
          e.stopPropagation();
          data.onAppendAfter(id);
        }}
        className="absolute -right-4 top-1/2 z-10 -translate-y-1/2 rounded-full border border-primary-400 bg-white dark:bg-neutral-800 p-1 text-primary-600 dark:text-primary-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:bg-primary-50 dark:hover:bg-primary-500/10"
      >
        <PlusIcon className="h-3.5 w-3.5" />
      </button>

      {/* Delete affordance */}
      <button
        type="button"
        title="Delete step"
        onClick={(e) => {
          e.stopPropagation();
          if (confirm(`Remove "${node.name ?? primitive.label}"?`)) data.onDelete(id);
        }}
        className="absolute -top-3 -right-3 z-10 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-1 text-neutral-400 dark:text-neutral-500 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-rose-600 dark:hover:text-rose-400"
      >
        <TrashIcon className="h-3 w-3" />
      </button>

      {/* Incoming handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!h-2 !w-2 !border-white dark:!border-neutral-800 !bg-neutral-400 dark:!bg-neutral-600"
      />

      {/* Header */}
      <div className="flex items-start gap-2.5 px-3 pt-3 pb-2">
        <span
          className={clsx(
            'flex h-7 w-7 flex-none items-center justify-center rounded-md ring-1 ring-inset',
            CATEGORY_ACCENT[primitive.category]
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-neutral-900 dark:text-white">
              {node.name ?? primitive.label}
            </span>
            {errors.length + warnings.length > 0 && (
              <span
                title={`${errors.length + warnings.length} issue${errors.length + warnings.length === 1 ? '' : 's'}`}
                className={clsx(
                  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                  errors.length > 0
                    ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300'
                    : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
                )}
              >
                <ExclamationTriangleIcon className="h-3 w-3" />
                {errors.length + warnings.length}
              </span>
            )}
          </div>
          <div className="truncate text-[11px] uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
            {primitive.shortLabel}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-3 pb-2 text-xs text-neutral-600 dark:text-neutral-400">
        {isUnconfiguredInput ? (
          <div className="space-y-2">
            <p className="text-neutral-500 dark:text-neutral-400">What kind of input?</p>
            <div className="grid grid-cols-2 gap-1">
              {INPUT_SUBTYPES.map((st) => {
                const StIcon = INPUT_SUBTYPE_ICON[st.key];
                return (
                  <button
                    key={st.key}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      data.onRequestSubtype(id, st.key);
                    }}
                    className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-300 hover:border-primary-400 hover:bg-primary-50/40 dark:hover:bg-primary-500/10"
                  >
                    <StIcon className="h-3.5 w-3.5 flex-none text-primary-600 dark:text-primary-400" />
                    <span className="truncate">{st.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : summary ? (
          <p className="line-clamp-2 text-neutral-600 dark:text-neutral-400">{summary}</p>
        ) : (
          <p className="italic text-neutral-400 dark:text-neutral-500">
            Configure in the inspector…
          </p>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap items-center gap-1 px-3 pb-3">
        {primitive.category === 'input' && node.inputSubtype && (
          <Badge tone="primary" icon={<InboxArrowDownIcon className="h-3 w-3" />}>
            Workflow input
          </Badge>
        )}
        {isEnumerable && (
          <Badge tone="indigo" icon={<ListBulletIcon className="h-3 w-3" />}>
            Per-item
          </Badge>
        )}
        {isOutputBatch && (
          <Badge tone="neutral" icon={<RectangleStackIcon className="h-3 w-3" />}>
            Combines list
          </Badge>
        )}
        {outputs.length > 0 && outputs.length <= 3 && (
          <div className="ml-auto flex items-center gap-1">
            {outputs.slice(0, 3).map((o) => (
              <span
                key={o.key}
                className="inline-flex items-center rounded-full bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 text-[10px] text-neutral-600 dark:text-neutral-400"
                title={o.description}
              >
                {o.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Dynamic output ports (virtual) */}
      {outputs.length > 0 &&
        (node.taskKey === 'EXTRACT' || node.taskKey === 'OUTPUT_INTEGRATION') && (
          <div className="border-t border-dashed border-neutral-200 dark:border-neutral-700 px-3 py-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Outputs
            </div>
            <div className="flex flex-wrap gap-1">
              {outputs.map((o) => (
                <span
                  key={o.key}
                  className="inline-flex items-center rounded-full bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 text-[10px] text-neutral-700 dark:text-neutral-300"
                  title={o.description}
                >
                  {o.label}
                </span>
              ))}
            </div>
          </div>
        )}

      {/* Outgoing handle(s) */}
      {outputs.map((o, i) => (
        <Handle
          key={o.key}
          id={o.key}
          type="source"
          position={Position.Right}
          style={{ top: `${30 + i * 8}%` }}
          className="!h-2 !w-2 !border-white dark:!border-neutral-800 !bg-neutral-400 dark:!bg-neutral-600"
        />
      ))}

      {/* Rename pencil (opens a prompt) */}
      {isSelected && (
        <button
          type="button"
          className="absolute top-2 right-2 rounded p-0.5 text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400"
          onClick={(e) => {
            e.stopPropagation();
            const next = prompt('Rename step', node.name ?? primitive.label);
            if (next !== null) data.onSelectNode(id); // selection already set; rename handled via inspector
            void next;
          }}
        >
          <PencilSquareIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Badge(props: {
  children: React.ReactNode;
  tone: 'primary' | 'indigo' | 'amber' | 'neutral';
  icon?: React.ReactNode;
}) {
  const tones = {
    primary: 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300',
    indigo: 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
    amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
    neutral: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400'
  } as const;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
        tones[props.tone]
      )}
    >
      {props.icon}
      {props.children}
    </span>
  );
}
